import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ApprovingInvoiceReport } from '../hooks/useBulkApproveReport';

type ReportTargetType = string | ApprovingInvoiceReport[];

type OpenConfirmApproveModalParams = {
  reportTarget: ReportTargetType;
  approveSuccessAction?: ApproveSuccessAction;
  approveType?: ApproveType;
};

type ConfirmApproveModalContextType = {
  reportTarget: ReportTargetType;
  approveType: ApproveType;
  onOpenConfirmApproveModal: (params: OpenConfirmApproveModalParams) => void;
  open: boolean;
  onCloseConfirmApproveModal: () => void;
  approveSuccessAction: ApproveSuccessAction;
};

export enum ApproveSuccessAction {
  GOTO_NEXT_TARGET = 'goto_next_target',
  REFRESH_DATA = 'refresh_data',
}

export enum ApproveType {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}

const Context = createContext<ConfirmApproveModalContextType>({
  reportTarget: '',
  approveType: ApproveType.SINGLE,
  onOpenConfirmApproveModal: () => {},
  open: false,
  onCloseConfirmApproveModal: () => {},
  approveSuccessAction: ApproveSuccessAction.REFRESH_DATA,
});

export const ConfirmApproveModalProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [reportTarget, setReportTarget] = useState<ReportTargetType>('');
  const [approveType, setApproveType] = useState<ApproveType>(
    ApproveType.SINGLE
  );
  const [open, setOpen] = useState<boolean>(false);
  const [approveSuccessAction, setApproveSuccessAction] =
    useState<ApproveSuccessAction>(ApproveSuccessAction.REFRESH_DATA);

  const onOpenConfirmApproveModal = useCallback(
    ({
      reportTarget,
      approveSuccessAction = ApproveSuccessAction.REFRESH_DATA,
      approveType = ApproveType.SINGLE,
    }: OpenConfirmApproveModalParams) => {
      setReportTarget(reportTarget);
      setOpen(true);
      setApproveSuccessAction(approveSuccessAction);
      setApproveType(approveType);
    },
    []
  );

  const onCloseConfirmApproveModal = useCallback(() => {
    setOpen(false);
    setReportTarget('');
    setApproveSuccessAction(ApproveSuccessAction.REFRESH_DATA);
    setApproveType(ApproveType.SINGLE);
  }, []);

  const value = useMemo(
    () => ({
      reportTarget,
      approveType,
      open,
      onOpenConfirmApproveModal,
      onCloseConfirmApproveModal,
      approveSuccessAction,
    }),
    [
      reportTarget,
      approveType,
      open,
      onOpenConfirmApproveModal,
      onCloseConfirmApproveModal,
      approveSuccessAction,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

ConfirmApproveModalProvider.displayName = 'ConfirmApproveModalProvider';

export const useConfirmApproveModal = () => useContext(Context);
